// eslint-disable-next-line @typescript-eslint/triple-slash-reference
///<reference path="../../External/JQuery/jqueryui/jqueryui.d.ts"/>
import { EventsController } from "Global/Dom/EventsController";
import { Event as GlobalEvent } from "../../../../Global/Base/Events";
import { IDisposable } from "../../../../Global/Base/IDisposable";
import { Globalization } from "../../../../Global/Dom/Globalization";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { Checkbox } from "../../../UI.BaseControls/Checkbox";
import { EventHelper } from "../../../UI.BaseControls/Helpers/EventHelper";
import { NumEdit } from "../../../UI.BaseControls/NumEdit";
import { Column } from "../../../UI.Controls/DataGridSubcontrols/Columns/Column";
import { IColumnPositionChangedEventData } from "../../../UI.Controls/IEvent";
import { ElementUtils } from "../../../UI.ElementarControls/Helpers/ElementUtils";
import { createElement, registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { IFrameColumnInfo, IFrameProfileInfo } from "../IFrameColumnInfo";
import { ILoader } from "../ILoader";
import { GenericListFrame } from "../XGControls/Frames/GenericListFrame";
import { IContentBuilder } from "./IContentBuilder";

const COLUMN_LISTENERS = "columnListeners";

/**
 * @internal
 */
export class ColumnInfoBuilder implements IContentBuilder, ILoader {
    private _element: HTMLElement;
    private _profileChangePreview: () => Promise<boolean>;
    private _frozenColumSubsection: HTMLElement;
    private _columnSection: HTMLElement;
    private _visibleColumnSubsection: HTMLElement;
    private _hiddenColumnSubsection: HTMLElement;
    private _columns: Column[];
    private _profileInfo: IFrameProfileInfo;
    private _frame: GenericListFrame;
    private _loaderElement: HTMLElement;
    private _showLoaderTimer: any;
    private _events: EventsController = new EventsController();

    /** Je vyvolána při změně v pořadí sloupců */
    public readonly columnPositionChanged: GlobalEvent<ColumnInfoBuilder, IColumnPositionChangedData> = new GlobalEvent<ColumnInfoBuilder, IColumnPositionChangedData>();
    /** Je vyvolána při změně viditelnosti sloupce */
    public readonly columnVisibilityChanged: GlobalEvent<ColumnInfoBuilder, IColumnVisibilityChangedData> = new GlobalEvent<ColumnInfoBuilder, IColumnVisibilityChangedData>();
    /** Je vyvolána při změně šířky sloupce */
    public readonly columnWidthChanged: GlobalEvent<ColumnInfoBuilder, IColumnWidthChangedData> = new GlobalEvent<ColumnInfoBuilder, IColumnWidthChangedData>();

    public get frame(): GenericListFrame {
        return this._frame;
    }

    public showLoader(delay: number = 300): void {
        if (this._showLoaderTimer)
            return;

        this._showLoaderTimer = self.setTimeout(() => {
            this._showLoader();
            this._showLoaderTimer = undefined;
        }, delay);
    }

    public hideLoader(): void {
        if (this._showLoaderTimer)
            clearTimeout(this._showLoaderTimer);
        this._showLoaderTimer = undefined;
        this._hideLoader();
    }

    protected _showLoader(): void {
        if (this._loaderElement)
            this._loaderElement.style.display = "";
    }

    protected _hideLoader(): void {
        if (this._loaderElement)
            this._loaderElement.style.display = "none";
    }

    public isLoading(): boolean {
        return this._loaderElement != null && this._loaderElement.style.display !== "none";
    }

    protected _createLoader(): void {
        this._loaderElement = createElement({ classes: "ElementLoader", css: { display: "none" } });
        const loader = createElement({ classes: "loader" });
        const right = createElement({ classes: "right" });
        const left = createElement({ classes: "left" });

        this._loaderElement.appendChild(loader);

        loader.appendChild(right);
        right.appendChild(createElement({ classes: "border" }));

        loader.appendChild(left);
        left.appendChild(createElement({ classes: "border" }));
    }

    constructor(columns: Column[], frame: GenericListFrame, profileChangePreview: () => Promise<boolean>) {
        this._columns = columns;
        this._frame = frame;
        this._profileInfo = frame.profileInfo;

        this._element = createElement({ classes: ["column-info-builder", "loader-enabled"] });

        this._columnSection = createElement({ classes: "column-section" });
        this._frozenColumSubsection = createElement({ classes: "frozen-column-section" });
        this._visibleColumnSubsection = createElement({ classes: "visible-column-section" });
        this._hiddenColumnSubsection = createElement({ classes: "hidden-column-section" });

        this._columnSection.appendChild(this._frozenColumSubsection);
        this._columnSection.appendChild(this._visibleColumnSubsection);
        this._columnSection.appendChild(this._hiddenColumnSubsection);

        this._element.appendChild(this._columnSection);

        this._events.add(this._frame.columnsUpdated, (_, { columns, profileInfo }) => this.reloadData(columns, profileInfo));
        this._events.add(this._frame.selectedProfileChanged, async (sourceFrame, profileInfo) => {
            if (sourceFrame instanceof GenericListFrame) {
                await this.reloadData(sourceFrame.dataGrid.columns, profileInfo);
            }
        });

        this._events.add(this._frame.editModeChanged, (_, value) => {
            $(this._visibleColumnSubsection).sortable(value ? "disable" : "enable");
        });

        this._events.add(this._frame.dataGrid.columnPositionChanged, async (_, { oldPosition, newPosition, oldColumnsVisibility, dontUpdate }) => {
            if (!dontUpdate) {
                this._updateColumnPosition(oldColumnsVisibility, oldPosition, newPosition);
            }
        });

        this._profileChangePreview = profileChangePreview;

        this._createLoader();
        this._element.appendChild(this._loaderElement);
    }

    public async reloadData(columns: Column[], profileInfo: IFrameProfileInfo): Promise<void> {
        this._columns = columns;
        this._profileInfo = profileInfo;
        this._frozenColumSubsection.innerHTML = "";
        this._visibleColumnSubsection.innerHTML = "";
        this._hiddenColumnSubsection.innerHTML = "";
        await this._loadData();
    }

    private async _sortHiddenColumnSection(): Promise<void> {
        const comp = new Intl.Collator(Globalization.actualCulture);
        const elements = Array.from(this._hiddenColumnSubsection.children) as ColumnInfoBuilderItem[];
        const columnNames = await Localization.getTexts(...elements.map(item => item.locKey));

        elements
            .sort((item1, item2) => comp.compare(columnNames.get(item1.locKey), columnNames.get(item2.locKey)))
            .forEach(item => this._hiddenColumnSubsection.appendChild(item));
    }

    /**
     * Upraví pozici sloupce v části "Nastavení sloupců". Je nutné zadat původní a aktuální pozici sloupce.
     * Do pozice je třeba zahrnout i sloupce které nejsou viditelné.
     * Pozice sloupce se indexuje od 1.
     * @param columnsVisibility Viditelnost sloupců datagridu před seřazením.
     * @param oldPosition Pozice v seznamu sloupců datagridu, na které se sloupec původně nacházel.
     * @param newPosition Pozice v seznamu sloupců datagridu, na které se sloupec aktuálně nachází.
     * @private
     */
    private _updateColumnPosition(columnsVisibility: boolean[], oldPosition: number, newPosition: number): void {
        if (oldPosition === newPosition) {
            return;
        }
        // Proměnná obsahuje nižší ze dvou pozic `oldPosition` a `newPosition`
        let lowPosition = oldPosition; // Pozícia s najnižším číslom
        // Proměnná obsahuje vyšší ze dvou pozic `oldPosition` a `newPosition`
        let highPosition = newPosition;

        // Příznak, zda se sloupec přesouvá zprava doleva.
        const isDraggedToTheLeft = newPosition < oldPosition;

        // V případě že se sloupec přesouvá zprava doleva, vymění se hodnoty nižší a vyšší pozice.
        if (isDraggedToTheLeft) {
            lowPosition = newPosition;
            highPosition = oldPosition;
        }

        const visibleColumnItems = Array.from(this._visibleColumnSubsection.children) as ColumnInfoBuilderItem[];
        // Vypočítá počet neviditelných sloupců před sloupcem na nižší pozici
        const invisibleColumnsBeforeLowPosition = columnsVisibility.slice(0, lowPosition - 1).filter(visible => !visible).length;
        // Vypočítá počet neviditelných sloupců před sloupcem na vyšší pozici
        const invisibleColumnsBeforeHighPosition = columnsVisibility.slice(0, highPosition - 1).filter(visible => !visible).length;

        // Zvolí sloupce na nižší a vyšší pozici
        const lowIndexColumn = visibleColumnItems[lowPosition - invisibleColumnsBeforeLowPosition - 1];
        const highIndexColumn = visibleColumnItems[highPosition - invisibleColumnsBeforeHighPosition - 1];

        if (isDraggedToTheLeft) {
            this._visibleColumnSubsection.insertBefore(highIndexColumn, lowIndexColumn);
        } else {
            this._visibleColumnSubsection.insertBefore(lowIndexColumn, highIndexColumn.nextSibling);
        }

        // Přepočítá hodnoty `order` podle nového umístění sloupce
        ColumnInfoBuilder._updateColumnItemsOrder(Array.from(this._visibleColumnSubsection.children) as ColumnInfoBuilderItem[]);
    }

    private async _loadData(): Promise<void> {
        this._showLoader();

        this._events.removeByKey(COLUMN_LISTENERS);

        for (const column of this._columns) {
            const attrName = column.attrName;
            const columnInfo: IFrameColumnInfo = this._profileInfo.columns[attrName];
            const columnItem = await ColumnInfoBuilderItem.initialize(column, columnInfo, this._frame, column.width);

            columnItem.order = column.order;

            this._events.addListenerWithKey(COLUMN_LISTENERS, columnItem, "visibleChange", async (e) => {
                const element = e.target as ColumnInfoBuilderItem;
                if (!(await this._profileChangePreview())) {
                    e.stopImmediatePropagation();
                    e.preventDefault();
                    element.visible = !element.visible;
                    return;
                }

                this.columnVisibilityChanged.trigger(this, { attrName: element.attrName, order: element.order, visible: element.visible });
            });

            this._events.addListenerWithKey(COLUMN_LISTENERS, columnItem, "widthChange", async (e: CustomEvent) => {
                const currentTarget = e.currentTarget;
                if (currentTarget == null || !(currentTarget instanceof ColumnInfoBuilderItem)) {
                    return;
                }
                if (!(await this._profileChangePreview())) {
                    e.stopImmediatePropagation();
                    e.preventDefault();
                    return;
                }
                EventHelper.stopPropagation(e);

                this.columnWidthChanged.trigger(this, { columnId: column.columnId, attrName: attrName, width: e.detail });
            }, { options: { capture: true } });

            this._events.addListenerWithKey(COLUMN_LISTENERS, column, "visibility", async (e: CustomEvent) => {
                const visible = e.detail.value;

                if (visible) {
                    const columnIndex = this._frame.dataGrid.columns.findIndex(column => column.attrName === columnItem.attrName);
                    this._visibleColumnSubsection.appendChild(columnItem);
                    const foundColumn = this._frame.dataGrid.columns[columnIndex];
                    const newPosition = this._frame.dataGrid.columns.length;
                    this._frame.dataGrid.setColumnOrder(foundColumn, newPosition);

                    for (const row of this._frame.dataGrid.dataRows) {
                        row.toggleVisibilityCellByAttrName(column.attrName, visible);
                    }

                    const eventData: IColumnPositionChangedEventData = {
                        oldColumnsVisibility: this._frame.dataGrid.columns.map(column => column.visible),
                        column: foundColumn,
                        newPosition: newPosition,
                        oldPosition: columnIndex + 1,
                        dontUpdate: true,
                    };
                    // pozice sloupců se indexuje od 1
                    this._frame.dataGrid.columnPositionChanged.trigger(this._frame.dataGrid, eventData);
                    this._frame.dataGrid.callEvent("ColumnPositionChanged", eventData);
                } else {
                    this._hiddenColumnSubsection.appendChild(columnItem);

                    this._sortHiddenColumnSection();
                }

                // Pro aktualizaci musíme znovu vyčíst potomky i s posledním přídaným/odebraným sloupcem
                ColumnInfoBuilder._updateColumnItemsOrder(Array.from(this._visibleColumnSubsection.children) as ColumnInfoBuilderItem[]);
            });

            if (columnItem.order <= this.frame.dataGrid.frozenColumnCount) {
                columnItem.checkbox.checked = true;
                this._frozenColumSubsection.appendChild(columnItem);
            } else if (columnItem.visible) {
                columnItem.checkbox.checked = true;
                this._visibleColumnSubsection.appendChild(columnItem);
            } else {
                columnItem.checkbox.checked = false;
                this._hiddenColumnSubsection.appendChild(columnItem);
            }
        }

        this._setSortable(this._visibleColumnSubsection);
        this._sortHiddenColumnSection();

        this._hideLoader();
    }

    private _setSortable(parentElement: HTMLElement): void {
        if ($(parentElement).sortable("instance"))
            $(parentElement).sortable("destroy");

        $(parentElement).sortable({
            helper: "clone", // Zabraňuje spuštění dalších událostí (click) // musi to tu zustat z duvodu FF, je treba ten helper upravit ve stylech
            placeholder: "ui-sortable-placeholder",
            start: (_, ui: JQueryUI.SortableUIParams) => {
                ui.helper.addClass("is-dragged");
                const columnItem = ((ui.item.get(0) as any) as ColumnInfoBuilderItem);
                const visibleColumnItems = Array.from(this._visibleColumnSubsection.children) as ColumnInfoBuilderItem[];
                columnItem.visibleOrder = visibleColumnItems.indexOf(columnItem) + 1 + this._frame.dataGrid.frozenColumnCount; // + 1 kvůli kompatibilitě s profily, které se počítají od 1
            },
            sort: () => null,
            stop: (_, ui: JQueryUI.SortableUIParams) => {
                const columnItem = ((ui.item.get(0) as any) as ColumnInfoBuilderItem);
                const oldOrder = columnItem.visibleOrder; // pořadí položky mezi viditelnými sloupci před 'tažením' myší pro přesunutí
                const newOrder = ui.item.index() + 1 + this._frame.dataGrid.frozenColumnCount; // + 1 kvůli kompatibilitě s profily, které se počítají od 1

                if (oldOrder === newOrder) {
                    return;
                }

                columnItem.order = newOrder;

                this.columnPositionChanged.trigger(this, { attrName: columnItem.attrName, newOrder: columnItem.order, oldOrder: oldOrder });

                ColumnInfoBuilder._updateColumnItemsOrder(Array.from(this._visibleColumnSubsection.children) as ColumnInfoBuilderItem[]);
            },
            scroll: true,
            items: "> .column-info-item",
            axis: "y",
            distance: 10,
        });
    }

    /**
     * Přepočítá order u viditelných sloupců podle jejich aktuálního pořadí
     * @param items Seznam sloupců
     */
    private static _updateColumnItemsOrder(items: ColumnInfoBuilderItem[]): void {
        items.forEach((item, index) => item.order = index + 1); // + 1 kvůli kompatibilitě s profily, které se počítají od 1
    }

    public async build(): Promise<HTMLElement> {
        await this._loadData();
        return this._element;
    }

    public dispose(): void {
        this._events.dispose();
    }
}

class ColumnInfoBuilderItem extends HTMLElement {
    private _checkBoxContainer: HTMLDivElement;
    private _checkboxElement: Checkbox;
    private _contentContainer: HTMLDivElement;
    private _contentMainRow: HTMLDivElement;
    private _nameElement: HTMLElement;
    private _moreSettingsButton: HTMLButtonElement;
    private _moreSettingBuilder: ColumnSettingsBuilder;
    private _moreSettingsContainer: HTMLDivElement;
    private _columnInfo: IFrameColumnInfo;
    private _isFrozenColumn: boolean;
    private _attrName: string;
    private _originalWidth: number | null = null;
    private _dragHandle: HTMLElement;

    private _frame: GenericListFrame;

    /*
     * Pořadí položky, které odpovídá pořadí sloupce na seznamu
     */
    public order: number = Number.MAX_VALUE;
    /*
     * Pořadí položky, které odpovídá pořadí mezi viditelnými položkami na panelu 'Nastavení sloupců'
     */
    public visibleOrder: number = Number.MAX_VALUE;

    public locKey: string;

    public get attrName(): string {
        return this._attrName;
    }

    public get checkbox(): Checkbox {
        return this._checkboxElement;
    }

    public get visible(): boolean {
        return this._checkboxElement.checked;
    }

    public set visible(v: boolean) {
        this._checkboxElement.checked = v;
    }

    public get columnInfo(): IFrameColumnInfo {
        if (!this._columnInfo && this.attrName)
            this._columnInfo = { attrName: this.attrName };
        return this._columnInfo;
    }

    public set columnInfo(v: IFrameColumnInfo) {
        this._columnInfo = v;
    }

    /**
     * Původní nezměněná šířka sloupce (z definice)
     */
    public get originalWidth(): number | null {
        return this._originalWidth;
    }

    public set originalWidth(value: number | null) {
        this._originalWidth = value;
    }

    public get frame(): GenericListFrame {
        return this._frame;
    }

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(): void {
        if (this.getAttribute("elementCreated") != null) // pokud vytváříme kopii elementu, nebudeme vytvářet všechny vnitřní elementy znovu; nastává při Drag'n'Drop přes JQuery UI Sortable Widget
            return;

        this.classList.add("column-info-item");

        this._contentContainer = createElement({ classes: "column-info-content" }) as HTMLDivElement;
        this._contentMainRow = createElement({ classes: "main-row" }) as HTMLDivElement;
        this._checkBoxContainer = createElement({ classes: "checkbox-container" }) as HTMLDivElement;

        this._checkboxElement = new Checkbox();
        this._checkboxElement.standardAction = true;
        this._checkboxElement.isNullable = false;

        this._nameElement = createElement({ classes: "name" });

        this._checkBoxContainer.appendChild(this._checkboxElement);
        this._checkBoxContainer.appendChild(this._nameElement);
        Localization.addLocDescData(this._checkboxElement, "GUI-669055", "title");//Viditelnost sloupce
        this._moreSettingsButton = createElement({ type: "button", classes: "more-settings-button" }) as HTMLButtonElement;
        this._moreSettingsButton.classList.add("btn", "flat", "icon", "ui-chevron-bottom");
        Localization.addLocDescData(this._moreSettingsButton, "GUI-669052", "title");//Další možnosti
        this._dragHandle = createElement({ classes: "icon" });
        this._contentMainRow.appendChild(this._dragHandle);
        this._contentMainRow.appendChild(this._checkBoxContainer);
        this._contentMainRow.appendChild(this._moreSettingsButton);

        this._contentContainer.appendChild(this._contentMainRow);

        this.appendChild(this._contentContainer);

        this.setAttribute("elementCreated", "");
    }

    public async initialize(column: Column, columnInfo: IFrameColumnInfo, frame: GenericListFrame, originalWidth: number | null = null): Promise<void> {
        this._frame = frame;
        this._isFrozenColumn = column.order <= frame.dataGrid.frozenColumnCount;
        this._dragHandle.classList.add(...(this._isFrozenColumn ? ["user-lock"] : ["drag-handle", "ui-drag-handle"]));
        this.checkbox.enabled = !this._isFrozenColumn;
        this._attrName = column.attrName;
        this.locKey = column.locKey;
        this.setAttribute("attrname", this._attrName);
        this._columnInfo = columnInfo;
        this.visible = column.visible;
        this._frame = frame;
        if (originalWidth != null) {
            this.originalWidth = originalWidth;
        }

        this._moreSettingBuilder = new ColumnSettingsBuilder(this);
        this._moreSettingsButton.addEventListener("click", async () => {
            if (this._moreSettingsButton.classList.contains("Disabled"))
                return;
            if (this._moreSettingsContainer == null) {
                this._contentMainRow.insertAdjacentElement("afterend", this._moreSettingsContainer = (await this._moreSettingBuilder.build()) as HTMLDivElement);
                this._moreSettingsButton.classList.add("ui-chevron-top");
                this._moreSettingsButton.classList.remove("ui-chevron-bottom");
                this.classList.add("more-settings-opened");
                Localization.addLocDescData(this._moreSettingsButton, "GUI-669053", "title");//Skrýt možnosti
            } else {
                this._moreSettingBuilder.dispose();
                this._moreSettingsContainer.remove();
                this._moreSettingsContainer = null;
                this._moreSettingsButton.classList.remove("ui-chevron-top");
                this._moreSettingsButton.classList.add("ui-chevron-bottom");
                this.classList.remove("more-settings-opened");
                Localization.addLocDescData(this._moreSettingsButton, "GUI-669052", "title");//Další možnosti
            }
        });

        this._checkBoxContainer.addEventListener("click", (e: MouseEvent) => {
            if (this._isFrozenColumn) {
                return;
            }
            this._checkboxElement.checked = !this._checkboxElement.checked;
            ElementUtils.callEvent(this, "visibleChange");
            e.preventDefault();
            EventHelper.stopImmediatePropagation(e);
        }, true);

        Localization.addLocData(this._nameElement, column.locKey, undefined, undefined, column.locParams);
    }

    public static async initialize(column: Column, columnInfo: IFrameColumnInfo, frame: GenericListFrame, originalWidth: number | null = null): Promise<ColumnInfoBuilderItem> {
        const cibi = new ColumnInfoBuilderItem();
        await cibi.initialize(column, columnInfo, frame, originalWidth);
        return cibi;
    }
}

class ColumnSettingsBuilder implements IContentBuilder, IDisposable {
    private _columnInfoBuilderItem: ColumnInfoBuilderItem;
    private _element: HTMLElement;

    private _widthDecrementButton: HTMLButtonElement;
    private _widthIncrementButton: HTMLButtonElement;
    private _widthNumEdit: NumEdit;

    private _frameProfileChangedHandler: () => void;

    constructor(columnInfoBuilderItem: ColumnInfoBuilderItem) {
        this._columnInfoBuilderItem = columnInfoBuilderItem;
    }

    private _getColumnWidth(profileColumnInfo: IFrameColumnInfo): number {
        return (profileColumnInfo && profileColumnInfo.width && (profileColumnInfo.width > Column.MIN_COLUMN_WIDTH ? profileColumnInfo.width : Column.MIN_COLUMN_WIDTH)) || this._columnInfoBuilderItem.originalWidth || Column.MIN_COLUMN_WIDTH;
    }

    private _reloadDataFromProfile(): void {
        const profileColumnInfo = this._columnInfoBuilderItem.frame.profileInfo.columns[this._columnInfoBuilderItem.attrName];
        this._columnInfoBuilderItem.columnInfo.width = this._getColumnWidth(profileColumnInfo);
        this._widthNumEdit.value = this._columnInfoBuilderItem.columnInfo.width;
    }

    private async _loadData(): Promise<void> {
        const settingsContainer = createElement({ classes: "more-column-settings" }) as HTMLDivElement;

        const widthLabel = createElement({ type: "span", classes: "setting-name" });
        Localization.addLocData(widthLabel, "GUI-655855"); // Šířka

        this._widthDecrementButton = createElement({ type: "button", classes: "settings-button" }) as HTMLButtonElement;
        this._widthDecrementButton.classList.add("btn", "flat", "icon", "ui-minus");
        Localization.addLocDescData(this._widthDecrementButton, "GUI-669050", "title");//Zmenšit šířku
        this._widthIncrementButton = createElement({ type: "button", classes: "settings-button" }) as HTMLButtonElement;
        this._widthIncrementButton.classList.add("btn", "flat", "icon", "ui-plus");
        Localization.addLocDescData(this._widthIncrementButton, "GUI-669051", "title");//Zvětšit šířku
        this._widthNumEdit = new NumEdit();
        this._widthNumEdit.wholeNumbers = true;
        Localization.addLocDescData(this._widthNumEdit, "GUI-655855", "title"); // Šířka
        settingsContainer.appendChild(widthLabel);
        settingsContainer.appendChild(this._widthDecrementButton);
        settingsContainer.appendChild(this._widthNumEdit);
        settingsContainer.appendChild(this._widthIncrementButton);

        if (!this._frameProfileChangedHandler)
            this._frameProfileChangedHandler = () => {
                const columnInfo: IFrameColumnInfo = this._columnInfoBuilderItem.frame.profileInfo.columns[this._columnInfoBuilderItem.attrName];
                this._columnInfoBuilderItem.columnInfo = columnInfo;
                this._widthNumEdit.value = this._columnInfoBuilderItem.columnInfo.width || this._getColumnWidth(columnInfo);
            };

        if (!this._columnInfoBuilderItem.frame.frameProfileChanged.containsListener(this._frameProfileChangedHandler))
            this._columnInfoBuilderItem.frame.frameProfileChanged.add(this._frameProfileChangedHandler);

        this._element = settingsContainer;

        const changeWidthFunction = (e: Event, widthValueGetter: () => number) => {
            EventHelper.stopImmediatePropagation(e);
            ElementUtils.callEvent(this._element, "widthChange", widthValueGetter());
        };

        const getWidthHandler = (positive: boolean, sender: HTMLButtonElement) => {
            return (e: MouseEvent) => {
                if (sender.classList.contains("Disabled"))
                    return;
                changeWidthFunction(e, () => {
                    const width = (this._columnInfoBuilderItem.columnInfo.width || this._columnInfoBuilderItem.originalWidth || 0) + ((positive ? 1 : -1) * 10);
                    return (width > Column.MIN_COLUMN_WIDTH ? width : Column.MIN_COLUMN_WIDTH);
                });
            };
        };

        this._widthDecrementButton.addEventListener("click", getWidthHandler(false, this._widthDecrementButton));
        this._widthIncrementButton.addEventListener("click", getWidthHandler(true, this._widthIncrementButton));

        this._reloadDataFromProfile();

        const applyWidth = (e: Event) => {
            changeWidthFunction(e, () => {
                const parsedWidth = Number(this._widthNumEdit.value);
                return (parsedWidth > Column.MIN_COLUMN_WIDTH ? parsedWidth : Column.MIN_COLUMN_WIDTH);
            });
        };
        this._widthNumEdit.addEventListener("keypress", (keyData: KeyboardEvent) => {
            if (keyData.key === "Enter") {
                applyWidth(keyData);
            }
        });
        this._widthNumEdit.addEventListener("blur", applyWidth);
    }

    public async build(): Promise<HTMLElement> {
        await this._loadData();
        return this._element;
    }

    public dispose(): void {
        this._columnInfoBuilderItem.frame.frameProfileChanged.remove(this._frameProfileChangedHandler);
    }
}

/** Data, předávaná událostí v případě změny pořadí sloupců */
export interface IColumnPositionChangedData {
    attrName: string;
    newOrder: number;
    oldOrder: number;
}

/** Data, předávaná událostí v případě změny viditelnosti sloupců */
export interface IColumnVisibilityChangedData {
    attrName: string;
    order: number;
    visible: boolean;
}

/** Data, předávaná událostí v případě změny šířky sloupců */
export interface IColumnWidthChangedData {
    columnId: string;
    attrName: string;
    width: number;
}

registerCustomElement("tsc-ColumnInfoBuilderItem", ColumnInfoBuilderItem);
